import {
  AdventureMismatchError,
  buildSaveFileForActiveAdventure,
  loadSaveFileForActiveAdventure,
  migrateSaveFile,
  type BuildSaveFileForActiveAdventureInput,
  type LoadSaveFileResult,
  type SaveFile,
  type SaveMetadata,
} from "./SaveFile.js";
import { getActiveAdventure } from "../adventure/ActiveAdventure.js";
import { clearPendingIntro } from "../narrative/introGate.js";
import {
  deleteSave,
  getSave,
  listSaves,
  putSave,
} from "./IDBStore.js";

/** Reserved slot IDs the UI pins at the top of the save list. */
export const AUTOSAVE_SLOT_ID = "autosave";
export const QUICKSAVE_SLOT_ID = "quicksave";

/**
 * Record shape stored inside each IndexedDB row. The `slotId` column that
 * IDBStore uses is the row key; this payload lives in `data`.
 */
interface StoredSlot {
  readonly displayName: string;
  readonly file: SaveFile;
}

/** Summary returned by `listSlots()` for the save/load UI. */
export interface SaveSlotSummary {
  readonly slotId: string;
  readonly displayName: string;
  readonly adventureId: string;
  readonly adventureVersion: string;
  readonly meta: SaveMetadata;
}

export class SlotNotFoundError extends Error {
  constructor(readonly slotId: string) {
    super(`Save slot "${slotId}" does not exist.`);
    this.name = "SlotNotFoundError";
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function isStoredSlot(v: unknown): v is StoredSlot {
  return (
    typeof v === "object" &&
    v !== null &&
    typeof (v as { displayName?: unknown }).displayName === "string" &&
    typeof (v as { file?: unknown }).file === "object"
  );
}

function defaultDisplayName(file: SaveFile): string {
  return `${file.adventureId} — ${file.meta.floorName} — ${file.meta.timestamp}`;
}

// ── Public API ────────────────────────────────────────────────────────────────

export interface SaveSlotInput extends BuildSaveFileForActiveAdventureInput {
  /** Optional friendly name. Defaults to `<adventureId> — <floor> — <timestamp>`. */
  readonly displayName?: string;
}

/**
 * Build a `SaveFile` from the live engine state (via
 * `buildSaveFileForActiveAdventure`) and persist it under `slotId`.
 * Overwrites any existing slot with the same id.
 */
export async function save(
  slotId: string,
  input: SaveSlotInput,
): Promise<SaveSlotSummary> {
  const file = buildSaveFileForActiveAdventure(input);
  const displayName = input.displayName ?? defaultDisplayName(file);
  const stored: StoredSlot = { displayName, file };
  await putSave(slotId, stored);
  return {
    slotId,
    displayName,
    adventureId: file.adventureId,
    adventureVersion: file.adventureVersion,
    meta: file.meta,
  };
}

/**
 * Read the slot, run migrations, enforce the active-adventure binding, and
 * apply the result to the live stores. Throws `SlotNotFoundError` if the
 * slot is missing, or the usual `SaveFile` errors on version/adventure
 * mismatch (see SaveFile.ts).
 */
export async function load(slotId: string): Promise<LoadSaveFileResult> {
  // A restored game never plays the new-game intro — disarm the gate in
  // case the player marked a new game and then loaded a save instead.
  clearPendingIntro();
  const raw = await getSave(slotId);
  if (raw === null) throw new SlotNotFoundError(slotId);
  if (!isStoredSlot(raw)) {
    // Older / hand-written slots may contain the SaveFile directly.
    return loadSaveFileForActiveAdventure(raw);
  }
  return loadSaveFileForActiveAdventure(raw.file);
}

/**
 * Return every stored slot's summary. Missing/corrupt rows are skipped rather
 * than throwing — the UI should still render the rest of the list if one
 * slot is damaged.
 */
export async function listSlots(): Promise<readonly SaveSlotSummary[]> {
  const ids = await listSaves();
  const summaries: SaveSlotSummary[] = [];
  for (const slotId of ids) {
    const raw = await getSave(slotId);
    if (raw === null) continue;
    if (isStoredSlot(raw)) {
      summaries.push({
        slotId,
        displayName: raw.displayName,
        adventureId: raw.file.adventureId,
        adventureVersion: raw.file.adventureVersion,
        meta: raw.file.meta,
      });
      continue;
    }
    // Legacy / direct SaveFile record.
    const file = raw as SaveFile;
    if (typeof file.adventureId !== "string") continue;
    summaries.push({
      slotId,
      displayName: defaultDisplayName(file),
      adventureId: file.adventureId,
      adventureVersion: file.adventureVersion,
      meta: file.meta,
    });
  }
  return summaries;
}

/**
 * Peek at a slot's migrated SaveFile without hydrating stores (P22).
 * Used by the reuse-party path to read a source roster without clobbering
 * the active PartyStore/WorldStore (which `load()` would do).
 * Throws `SlotNotFoundError` if the slot is missing.
 */
export async function peek(slotId: string): Promise<SaveFile> {
  const raw = await getSave(slotId);
  if (raw === null) throw new SlotNotFoundError(slotId);
  const file = isStoredSlot(raw) ? raw.file : (raw as SaveFile);
  return migrateSaveFile(file);
}

/** Delete the slot. No-op if the slot does not exist. */
export async function deleteSlot(slotId: string): Promise<void> {
  await deleteSave(slotId);
}

/**
 * Rename an existing slot. Throws `SlotNotFoundError` if the slot does not
 * exist. The underlying `SaveFile` is left untouched.
 */
export async function renameSlot(slotId: string, displayName: string): Promise<void> {
  const raw = await getSave(slotId);
  if (raw === null) throw new SlotNotFoundError(slotId);
  const file = isStoredSlot(raw) ? raw.file : (raw as SaveFile);
  const stored: StoredSlot = { displayName, file };
  await putSave(slotId, stored);
}

// ── Export / Import (P10) ─────────────────────────────────────────────────────

/**
 * Envelope for an exported save. Wraps the raw SaveFile with a display name
 * and a schema-stamp so an older import tool can refuse a future format.
 */
interface ExportedSave {
  readonly kind: "bkcrawl/save-export";
  readonly exportVersion: 1;
  readonly displayName: string;
  readonly file: SaveFile;
}

export class SaveImportError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "SaveImportError";
  }
}

/**
 * Serialize a stored slot to a `.json` string the player can download.
 * Throws `SlotNotFoundError` if the slot is missing.
 */
export async function exportSlot(slotId: string): Promise<string> {
  const raw = await getSave(slotId);
  if (raw === null) throw new SlotNotFoundError(slotId);
  const file: SaveFile = isStoredSlot(raw) ? raw.file : (raw as SaveFile);
  const displayName = isStoredSlot(raw) ? raw.displayName : defaultDisplayName(file);
  const envelope: ExportedSave = {
    kind: "bkcrawl/save-export",
    exportVersion: 1,
    displayName,
    file,
  };
  return JSON.stringify(envelope);
}

/** Browser-friendly wrapper: returns a Blob the UI can feed to `URL.createObjectURL`. */
export async function exportSlotAsBlob(slotId: string): Promise<Blob> {
  const json = await exportSlot(slotId);
  return new Blob([json], { type: "application/json" });
}

/**
 * Parse an exported save envelope and store it under `slotId`. Runs the
 * standard migration + adventure-binding validation via
 * `buildSaveFileForActiveAdventure` (same path the load screen uses) so a
 * save from the wrong adventure is rejected before it lands in the DB.
 *
 * Returns the summary for the freshly-imported slot.
 */
export async function importSlot(
  slotId: string,
  json: string,
): Promise<SaveSlotSummary> {
  let raw: unknown;
  try {
    raw = JSON.parse(json) as unknown;
  } catch (e) {
    throw new SaveImportError(`Not valid JSON: ${(e as Error).message}`);
  }

  // Accept either the envelope shape or a bare SaveFile (hand-authored).
  let rawFile: unknown;
  let displayName: string | null = null;
  if (
    typeof raw === "object" &&
    raw !== null &&
    (raw as { kind?: unknown }).kind === "bkcrawl/save-export"
  ) {
    const env = raw as Partial<ExportedSave>;
    rawFile = env.file;
    if (typeof env.displayName === "string") displayName = env.displayName;
  } else {
    rawFile = raw;
  }

  const file = migrateSaveFile(rawFile);
  const { manifest } = getActiveAdventure();
  if (file.adventureId !== manifest.id) {
    throw new AdventureMismatchError(manifest.id, file.adventureId);
  }
  const stored: StoredSlot = {
    displayName: displayName ?? defaultDisplayName(file),
    file,
  };
  await putSave(slotId, stored);
  return {
    slotId,
    displayName: stored.displayName,
    adventureId: file.adventureId,
    adventureVersion: file.adventureVersion,
    meta: file.meta,
  };
}
